
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../base/Button';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProductsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Über Uns', href: '/ueber-uns' },
    { name: 'Einsatzbereiche', href: '/einsatzbereiche' },
    { name: 'Kontakt', href: '/kontakt' }
  ];

  const productCategories = [
    { name: 'Flaggen', href: '/flaggen', icon: 'ri-flag-line', description: 'Professionelle Fahnen & Banner' },
    { name: 'Werbung', href: '/werbung', icon: 'ri-advertisement-line', description: 'Werbemittel & Displays' },
    { name: 'Promotion', href: '/promotion', icon: 'ri-gift-line', description: 'Promotion-Artikel & Giveaways' }
  ];

  // On non-home pages, always show white background
  const shouldShowWhiteBg = !isHomePage || isScrolled;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      shouldShowWhiteBg ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/4452e737fc4e63aeddff086153f9ecb8.png" 
              alt="Horizon Signage" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.slice(0, 2).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-base font-medium transition-colors duration-300 ${
                  location.pathname === item.href
                    ? 'text-[#254a29]'
                    : shouldShowWhiteBg
                    ? 'text-gray-800 hover:text-[#254a29]'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Products Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
                className={`flex items-center text-base font-medium transition-colors duration-300 cursor-pointer ${
                  ['/flaggen', '/werbung', '/promotion'].includes(location.pathname)
                    ? 'text-[#254a29]'
                    : shouldShowWhiteBg
                    ? 'text-gray-800 hover:text-[#254a29]'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                Produkte
                <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${isProductsDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown Menu */}
              <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 ${
                isProductsDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                <div className="p-2">
                  {productCategories.map((category) => (
                    <Link
                      key={category.name}
                      to={category.href}
                      onClick={() => setIsProductsDropdownOpen(false)}
                      className={`flex items-center p-3 rounded-lg transition-all duration-200 group ${
                        location.pathname === category.href
                          ? 'bg-green-50 text-[#254a29]'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg mr-3 transition-colors ${
                        location.pathname === category.href
                          ? 'bg-[#254a29] text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-[#254a29] group-hover:text-white'
                      }`}>
                        <i className={`${category.icon} text-lg`}></i>
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{category.name}</div>
                        <div className="text-xs text-gray-500">{category.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {navigation.slice(2).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-base font-medium transition-colors duration-300 ${
                  location.pathname === item.href
                    ? 'text-[#254a29]'
                    : shouldShowWhiteBg
                    ? 'text-gray-800 hover:text-[#254a29]'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Button href="/kontakt" size="md">
              Angebot anfordern
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md transition-colors duration-300 ${
                shouldShowWhiteBg
                  ? 'text-gray-800 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                <span className={`block w-6 h-0.5 bg-current transform transition-transform duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-1' : ''
                }`}></span>
                <span className={`block w-6 h-0.5 bg-current transition-opacity duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}></span>
                <span className={`block w-6 h-0.5 bg-current transform transition-transform duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-1' : ''
                }`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="py-4 space-y-1 bg-white shadow-lg rounded-lg mt-2">
            <Link
              to="/"
              className={`block px-4 py-2 text-base font-medium transition-colors duration-300 ${
                location.pathname === '/'
                  ? 'text-[#254a29] bg-green-50'
                  : 'text-gray-800 hover:text-[#254a29] hover:bg-gray-50'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/ueber-uns"
              className={`block px-4 py-2 text-base font-medium transition-colors duration-300 ${
                location.pathname === '/ueber-uns'
                  ? 'text-[#254a29] bg-green-50'
                  : 'text-gray-800 hover:text-[#254a29] hover:bg-gray-50'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Über Uns
            </Link>
            
            {/* Mobile Products Section */}
            <div className="px-4 py-2">
              <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Produkte</div>
              <div className="space-y-1 pl-2">
                {productCategories.map((category) => (
                  <Link
                    key={category.name}
                    to={category.href}
                    className={`flex items-center py-2 text-base font-medium transition-colors duration-300 ${
                      location.pathname === category.href
                        ? 'text-[#254a29]'
                        : 'text-gray-700 hover:text-[#254a29]'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className={`${category.icon} mr-2`}></i>
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/einsatzbereiche"
              className={`block px-4 py-2 text-base font-medium transition-colors duration-300 ${
                location.pathname === '/einsatzbereiche'
                  ? 'text-[#254a29] bg-green-50'
                  : 'text-gray-800 hover:text-[#254a29] hover:bg-gray-50'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Einsatzbereiche
            </Link>
            <Link
              to="/kontakt"
              className={`block px-4 py-2 text-base font-medium transition-colors duration-300 ${
                location.pathname === '/kontakt'
                  ? 'text-[#254a29] bg-green-50'
                  : 'text-gray-800 hover:text-[#254a29] hover:bg-gray-50'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Kontakt
            </Link>
            <div className="px-4 pt-2">
              <Button href="/kontakt" size="md" className="w-full">
                Angebot anfordern
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
